import React, { useState } from 'react';
import { FaPlus, FaTrash, FaFileAlt, FaEye, FaArrowLeft } from 'react-icons/fa';
import Swal from 'sweetalert2';
import DJ1835BienesRaicesTab from './DJ1835BienesRaicesTab';
import DJ1879HonorariosTab from './DJ1879HonorariosTab';
import DJ1887RemuneracionesTab from './DJ1887RemuneracionesTab';
import DJ1948RetirosDividendosTab from './DJ1948RetirosDividendosTab';
import DJ1929InversionesExteriorTab from './DJ1929InversionesExteriorTab';
import DJ1947TransparenciaTab from './DJ1947TransparenciaTab';
import { djAplica } from '../../services/regimenesTributarios';
import '../../styles/components/DeclaracionesJuradasTab.css';

/**
 * Componente para gestionar Declaraciones Juradas
 * Diseñado de manera modular para ser usado por agentes autónomos
 *
 * @param {Object} props
 * @param {Array} props.declaraciones - Lista de declaraciones juradas
 * @param {number} props.selectedYear - Año seleccionado
 * @param {boolean} props.loading - Estado de carga
 * @param {Function} props.onCreate - Callback para crear nueva declaración
 * @param {Function} props.onUpdate - Callback para actualizar declaración
 * @param {Function} props.onDelete - Callback para eliminar declaración
 * @param {string} props.token - Token de autenticación
 * @param {number} props.idCompany - ID de la empresa
 * @param {string} props.rutEmpresa - RUT de la empresa
 * @param {string|null} props.regimen - Régimen tributario de la empresa (14d3, 14d8, …)
 */
const DeclaracionesJuradasTab = ({
    declaraciones = [],
    selectedYear,
    loading = false,
    onCreate,
    onUpdate,
    onDelete,
    token,
    idCompany,
    rutEmpresa,
    regimen = null
}) => {
    const [showModal, setShowModal] = useState(false);
    const [editingDeclaracion, setEditingDeclaracion] = useState(null);
    const [selectedDeclaracion, setSelectedDeclaracion] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        tipo: ''
    });

    /**
     * Tipos de declaraciones juradas disponibles
     * Puede ser configurado externamente por un agente
     */
    const tiposDeclaracion = [
        { value: 'F1948', label: 'DJ 1948 - Declaración de Retiros y Dividendos' },
        { value: 'DJ1835', label: 'DJ 1835 - Sobre Bienes Raíces Arrendados' },
        { value: 'DJ1879', label: 'DJ 1879 Anual Sobre Retenciones Efectuadas Conforme A Los Arts. 42° N° 2 Y 48° De La L.I.R.' },
        { value: 'DJ1887', label: 'DJ 1887 - remuneraciones' },
        { value: 'DJ1929', label: 'DJ 1929 - Inversiones y rentas en el exterior' },
        { value: 'DJ1947', label: 'DJ 1947 - Base imponible, créditos y PPM de los propietarios (14 D N°8)' }
    ];

    // Solo las DJ del régimen de la empresa; al editar se mantiene el tipo que ya tenía
    const tiposDelRegimen = tiposDeclaracion.filter(
        (tipo) => djAplica(regimen, tipo.value) || tipo.value === editingDeclaracion?.tipo
    );

    const handleOpenModal = (declaracion = null) => {
        if (declaracion) {
            setEditingDeclaracion(declaracion);
            setFormData({
                tipo: declaracion.tipo || ''
            });
        } else {
            setEditingDeclaracion(null);
            setFormData({
                tipo: ''
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingDeclaracion(null);
        setSubmitting(false);
        setFormData({
            tipo: ''
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Evitar múltiples envíos
        if (submitting) return;

        // Validación básica
        if (!formData.tipo) {
            Swal.fire({
                icon: 'warning',
                title: 'Campos requeridos',
                text: 'Debe seleccionar un tipo de declaración'
            });
            return;
        }

        setSubmitting(true);
        try {
            if (editingDeclaracion) {
                await onUpdate(editingDeclaracion.id, formData);
                Swal.fire({
                    icon: 'success',
                    title: 'Actualizado',
                    text: 'Declaración jurada actualizada exitosamente'
                });
            } else {
                await onCreate(formData);
                Swal.fire({
                    icon: 'success',
                    title: 'Creado',
                    text: 'Declaración jurada creada exitosamente'
                });
            }
            handleCloseModal();
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'Error al guardar declaración jurada'
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (declaracion) => {
        const result = await Swal.fire({
            icon: 'warning',
            title: '¿Está seguro?',
            text: 'Esta acción no se puede deshacer',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await onDelete(declaracion.id);
                Swal.fire({
                    icon: 'success',
                    title: 'Eliminado',
                    text: 'Declaración jurada eliminada exitosamente'
                });
            } catch (error) {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: error.message || 'Error al eliminar declaración jurada'
                });
            }
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-CL');
    };

    const handleViewDetails = (declaracion) => {
    if (declaracion.tipo === 'DJ1835' || declaracion.tipo === 'DJ1879' || declaracion.tipo === 'DJ1887' || declaracion.tipo === 'F1948' || declaracion.tipo === 'DJ1929' || declaracion.tipo === 'DJ1947') {
        setSelectedDeclaracion(declaracion);
        } else {
            Swal.fire({
                icon: 'info',
                title: 'Funcionalidad no disponible',
                text: `El detalle para ${declaracion.tipo} estará disponible próximamente`
            });
        }
    };

    const handleBackToList = () => {
        setSelectedDeclaracion(null);
    };

    // Si hay una declaración seleccionada de tipo DJ1835, mostrar el componente específico
    if (selectedDeclaracion && selectedDeclaracion.tipo === 'DJ1835') {
        return (
            <div className="declaraciones-juradas-tab">
                <div className="back-button-container">
                    <button className="btn-back" onClick={handleBackToList}>
                        <FaArrowLeft /> Volver a Declaraciones Juradas
                    </button>
                </div>
                <DJ1835BienesRaicesTab
                    declaracionId={selectedDeclaracion.id}
                    year={selectedYear}
                    idCompany={idCompany}
                    rutEmpresa={rutEmpresa}
                    token={token}
                />
            </div>
        );
    }

    // Si hay una declaración seleccionada de tipo DJ1879, mostrar el componente específico
    if (selectedDeclaracion && selectedDeclaracion.tipo === 'DJ1879') {
        return (
            <div className="declaraciones-juradas-tab">
                <div className="back-button-container">
                    <button className="btn-back" onClick={handleBackToList}>
                        <FaArrowLeft /> Volver a Declaraciones Juradas
                    </button>
                </div>
                <DJ1879HonorariosTab
                    declaracionId={selectedDeclaracion.id}
                    year={selectedYear}
                    idCompany={idCompany}
                    rutEmpresa={rutEmpresa}
                    token={token}
                />
            </div>
        );
    }
    if (selectedDeclaracion && selectedDeclaracion.tipo === 'DJ1887') {
        return (
            <div className="declaraciones-juradas-tab">
                <div className="back-button-container">
                    <button className="btn-back" onClick={handleBackToList}>
                        <FaArrowLeft /> Volver a Declaraciones Juradas
                    </button>
                </div>
                <DJ1887RemuneracionesTab
                    declaracionId={selectedDeclaracion.id}
                    year={selectedYear}
                    idCompany={idCompany}
                    rutEmpresa={rutEmpresa}
                    token={token}
                />
            </div>
        );
    }

    if (selectedDeclaracion && selectedDeclaracion.tipo === 'F1948') {
        return (
            <div className="declaraciones-juradas-tab">
                <div className="back-button-container">
                    <button className="btn-back" onClick={handleBackToList}>
                        <FaArrowLeft /> Volver a Declaraciones Juradas
                    </button>
                </div>
                <DJ1948RetirosDividendosTab
                    declaracionId={selectedDeclaracion.id}
                    year={selectedYear}
                    idCompany={idCompany}
                    rutEmpresa={rutEmpresa}
                    token={token}
                />
            </div>
        );
    }

    if (selectedDeclaracion && selectedDeclaracion.tipo === 'DJ1929') {
        return (
            <div className="declaraciones-juradas-tab">
                <div className="back-button-container">
                    <button className="btn-back" onClick={handleBackToList}>
                        <FaArrowLeft /> Volver a Declaraciones Juradas
                    </button>
                </div>
                <DJ1929InversionesExteriorTab
                    year={selectedYear}
                    idCompany={idCompany}
                    rutEmpresa={rutEmpresa}
                    token={token}
                />
            </div>
        );
    }

    if (selectedDeclaracion && selectedDeclaracion.tipo === 'DJ1947') {
        return (
            <div className="declaraciones-juradas-tab">
                <div className="back-button-container">
                    <button className="btn-back" onClick={handleBackToList}>
                        <FaArrowLeft /> Volver a Declaraciones Juradas
                    </button>
                </div>
                <DJ1947TransparenciaTab
                    declaracion={selectedDeclaracion}
                    year={selectedYear}
                    idCompany={idCompany}
                    rutEmpresa={rutEmpresa}
                    token={token}
                />
            </div>
        );
    }

    return (
        <div className="declaraciones-juradas-tab">
            {/* Header */}
            <div className="tab-header">
                <div className="tab-title">
                    <FaFileAlt className="title-icon" />
                    <h2>Declaraciones Juradas - Año {selectedYear}</h2>
                </div>
                <button
                    className="btn-nuevo"
                    onClick={() => handleOpenModal()}
                    disabled={loading}
                >
                    <FaPlus /> Nueva Declaración Jurada
                </button>
            </div>

            {/* Tabla de declaraciones */}
            <div className="table-container">
                {loading ? (
                    <div className="loading-message">Cargando...</div>
                ) : declaraciones.length === 0 ? (
                    <div className="empty-message">
                        <FaFileAlt className="empty-icon" />
                        <p>No hay declaraciones juradas para el año {selectedYear}</p>
                        <button className="btn-primary" onClick={() => handleOpenModal()}>
                            <FaPlus /> Crear Primera Declaración
                        </button>
                    </div>
                ) : (
                    <table className="declaraciones-table">
                        <thead>
                            <tr>
                                <th>Tipo</th>
                                <th>Fecha Presentación</th>
                                <th>Estado</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {declaraciones.map((declaracion) => (
                                <tr key={declaracion.id}>
                                    <td>{declaracion.tipo}</td>
                                    <td>{formatDate(declaracion.fecha_presentacion)}</td>
                                    <td>
                                        <span className={`estado-badge estado-${declaracion.estado || 'pendiente'}`}>
                                            {declaracion.estado || 'Pendiente'}
                                        </span>
                                    </td>
                                    <td className="actions-cell">

                                        {(declaracion.tipo === 'DJ1835' || declaracion.tipo === 'DJ1879' || declaracion.tipo === 'DJ1887' || declaracion.tipo === 'F1948' || declaracion.tipo === 'DJ1929' || declaracion.tipo === 'DJ1947') && (
                                            <button
                                                className="btn-icon btn-view"
                                                onClick={() => handleViewDetails(declaracion)}
                                                title="Ver detalles"
                                            >
                                                <FaEye />
                                            </button>
                                        )}
<button
                                            className="btn-icon btn-delete"
                                            onClick={() => handleDelete(declaracion)}
                                            title="Eliminar"
                                        >
                                            <FaTrash />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Modal de formulario */}
            {showModal && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{editingDeclaracion ? 'Editar' : 'Nueva'} Declaración Jurada</h3>
                            <button className="btn-close" onClick={handleCloseModal}>×</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="form-grid">
                                <div className="form-group full-width">
                                    <label htmlFor="tipo">Tipo de Declaración *</label>
                                    <select
                                        id="tipo"
                                        name="tipo"
                                        value={formData.tipo}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Seleccione...</option>
                                        {tiposDelRegimen.map(tipo => (
                                            <option key={tipo.value} value={tipo.value}>
                                                {tipo.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn-secondary" onClick={handleCloseModal} disabled={submitting}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-primary" disabled={submitting}>
                                    {submitting ? 'Procesando...' : (editingDeclaracion ? 'Actualizar' : 'Crear')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DeclaracionesJuradasTab;
